'use client';

import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { ChevronDown } from 'lucide-react';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import { tierColors, tierOrder, type Tier } from '@/lib/mtf';

interface TooltipPayloadItem {
  dataKey: string;
  value: number;
  fill: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  const { t, fmtNum } = useLanguage();
  if (!active || !payload || payload.length === 0) return null;

  // Find the segment that was hovered (last non-zero in payload or the one with value)
  const hovered = payload.find(p => p.value > 0);
  if (!hovered) return null;

  const tierKey = hovered.dataKey as string; // e.g. "tiers.tier0"
  const tierNum = parseInt(tierKey.replace('tiers.tier', ''), 10) as Tier;

  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-lg"
      style={{
        backgroundColor: 'var(--surface-card)',
        borderColor: 'var(--border)',
        color: 'var(--foreground)',
        minWidth: 160,
      }}
    >
      <div className="font-semibold mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
          style={{ backgroundColor: tierColors[tierNum] }}
        />
        <span>{t('tier.' + tierNum)}</span>
        <span className="ml-auto font-bold tabular-nums">{fmtNum(hovered.value)}%</span>
      </div>
    </div>
  );
}

export default function TierStackedBar() {
  const { allCountries, profile } = useCountry();
  const { t, fmtNum } = useLanguage();
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  function toggleRow(id: string) {
    setOpenRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // Build chart data: each row has country name + tiers.tier0..tier5
  const chartData = allCountries.map(cc => ({
    country: cc.country,
    id: cc.id,
    'tiers.tier0': cc.tiers.tier0,
    'tiers.tier1': cc.tiers.tier1,
    'tiers.tier2': cc.tiers.tier2,
    'tiers.tier3': cc.tiers.tier3,
    'tiers.tier4': cc.tiers.tier4,
    'tiers.tier5': cc.tiers.tier5,
  }));

  return (
    <div>
      {/* Stacked Bar Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
        >
          <XAxis
            type="number"
            domain={[0, 100]}
            unit="%"
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="country"
            width={110}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />
          {tierOrder.map(tier => {
            const dataKey = `tiers.tier${tier}`;
            return (
              <Bar
                key={dataKey}
                dataKey={dataKey}
                stackId="tiers"
                fill={tierColors[tier]}
                isAnimationActive={false}
              >
                {tier === 5
                  ? chartData.map(entry => {
                      const isSelected = entry.country === profile?.name;
                      return (
                        <Cell
                          key={entry.id}
                          fill={tierColors[5]}
                          stroke={isSelected ? 'var(--accent)' : 'transparent'}
                          strokeWidth={isSelected ? 2 : 0}
                        />
                      );
                    })
                  : null}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
        {tierOrder.map(tier => (
          <div key={tier} className="flex items-center gap-1.5">
            <span
              className="inline-block rounded-sm flex-shrink-0"
              style={{
                width: 10,
                height: 10,
                backgroundColor: tierColors[tier],
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
              {t('tier.' + tier)}
            </span>
          </div>
        ))}
      </div>

      {/* Disclaimer */}
      <p
        className="mt-2"
        style={{ fontSize: 11, color: 'var(--muted-foreground)' }}
      >
        {t('mtf.disclaimer')}
      </p>

      {/* Attribute Accordion */}
      <div className="mt-4 border rounded-lg overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {allCountries.map(cc => {
          const isOpen = openRows.has(cc.id);
          const attrs: Array<{ key: string; value: number }> = [
            { key: 'capacity', value: cc.attributes.capacity },
            { key: 'duration', value: cc.attributes.duration },
            { key: 'reliability', value: cc.attributes.reliability },
            { key: 'affordability', value: cc.attributes.affordability },
          ];

          return (
            <div key={cc.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={() => toggleRow(cc.id)}
                className="w-full flex items-center justify-between px-4 text-sm font-medium transition-colors hover:bg-surface-light"
                style={{
                  height: 40,
                  color: 'var(--foreground)',
                  backgroundColor: 'transparent',
                  textAlign: 'left',
                }}
                aria-expanded={isOpen}
              >
                <span>{cc.country}</span>
                <ChevronDown
                  size={16}
                  style={{
                    color: 'var(--muted-foreground)',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 220ms ease-out',
                  }}
                />
              </button>

              {/* Accordion panel with max-height transition */}
              <div
                style={{
                  maxHeight: isOpen ? 200 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 220ms ease-out',
                }}
                className="motion-reduce:transition-none"
              >
                <div className="px-4 pb-3 pt-1 space-y-2">
                  {attrs.map(attr => (
                    <div key={attr.key} className="flex items-center gap-3">
                      <span
                        className="text-xs flex-shrink-0"
                        style={{ color: 'var(--muted-foreground)', width: 130 }}
                      >
                        {t('attribute.' + attr.key)}
                      </span>
                      <div className="flex-1 rounded-full overflow-hidden" style={{ height: 6, backgroundColor: 'var(--muted)' }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(attr.value / 5) * 100}%`,
                            backgroundColor: 'var(--primary)',
                          }}
                        />
                      </div>
                      <span
                        className="text-xs font-semibold tabular-nums flex-shrink-0"
                        style={{ color: 'var(--foreground)', width: 28, textAlign: 'right' }}
                      >
                        {fmtNum(attr.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
